/**
 * Get the current month data in daily format for the interactions and users for the chart
 * @returns {Array} The current month data
 */
import { atEndOfDay, atStartOfDay, getDaysDiff } from '@common/helpers/date.helper'
import { months } from '@common/constants/time.constant'
import { FabTypes } from '@/components/analytics/customerCustom/fab.types'

export function getFullDataDaily(dataPerMonth: any, currentPeriod: Date[]): Array<{ label: string, interactions: number, users: number, date: Date }> {
  const [firstDate, lastDate] = currentPeriod
  const now = new Date()
  const minDays = 30

  const diffDays = getDaysDiff(firstDate, lastDate)

  let startDate = new Date(firstDate)
  let endDate = new Date(lastDate)

  if (diffDays < minDays) {
    const missingDays = minDays - diffDays
    const halfMissing = Math.floor(missingDays / 2)

    // Extend startDate backward
    startDate.setDate(startDate.getDate() - halfMissing)

    // Extend endDate forward
    const endExtension = missingDays - halfMissing
    const maxEndDate = new Date(Math.min(now.getTime(), endDate.getTime() + endExtension * 24 * 60 * 60 * 1000))

    const actualEndExtension = Math.floor((maxEndDate.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24))
    const leftoverDays = endExtension - actualEndExtension

    endDate = maxEndDate

    // Compensate any leftover extension by adjusting startDate further
    if (leftoverDays > 0) {
      startDate.setDate(startDate.getDate() - leftoverDays)
    }
  }

  const result = []
  const cursor = new Date(startDate)

  while (cursor <= endDate) {
    const key = getDateKey(cursor)
    const monthData = dataPerMonth[key]?.dailyStatistics || {}
    const day = String(cursor.getDate()).padStart(2, '0')
    const month = String(cursor.getMonth() + 1).padStart(2, '0')
    const index = `${day}-${month}-${cursor.getFullYear()}`

    result.push({
      label: `${day}-${month}`,
      interactions: monthData[index]?.interactions || 0,
      users: Object.keys(monthData[index]?.users || {}).length || 0,
      date: new Date(cursor)
    })

    cursor.setDate(cursor.getDate() + 1)
  }

  return result
}

export async function loadPeriodData(startDate: Date, endDate: Date, minDate: Date, loadYearly: boolean, loadData: (month: number, year: number, key: string) => Promise<void>) {
  let initialDate = new Date(startDate.getFullYear(), startDate.getMonth() - (loadYearly ? 11 : 2), startDate.getDate())

  if (initialDate < minDate) {
    initialDate = minDate
  }

  try {
    const monthsToLoad: any[] = []
    const cursor = new Date(endDate.getFullYear(), endDate.getMonth(), 1)

    while (cursor >= initialDate) {
      const month = cursor.getMonth()
      const year = cursor.getFullYear()
      const key = `${month}-${year}`

      monthsToLoad.push({ month, year, key })

      cursor.setMonth(cursor.getMonth() - 1)
    }

    for (const index in monthsToLoad) {
      const { month, year, key } = monthsToLoad[index]
      setTimeout(async () => {
        await loadData(month, year, key)
      }, +index * 500)
    }

  } catch (error) {
    console.error(error)
  }
}

export function getPeriodData(dataPerMonth: any, startDate: Date, endDate: Date) {
  const result: any = {
    interactions: 0,
    uniqueUsers: new Set(),
    sentiment: 0,
    satisfaction: 0,
    retention: 0,
    chats: 0,
    totalSeconds: 0,
    conversations: 0,
    seconds: 0,
    presentedRatings: [],
    answeredRatingCards: 0,
    ratingResults: {},
    channels: {},
    performance: null,
    interactionsPerUser: {},
    popularIntents: [],
    questionsPerNlu: null,
    trainedPerformance: null,
    untrainedPerformance: null,
    llmData: [],
    startDate: startDate,
    endDate: endDate
  }

  let count = 0

  const combine = (obj1: {}, obj2?: {}) =>
    Object.fromEntries(
      [...new Set([...Object.keys(obj1), ...Object.keys(obj2 || {})])].map(
        key => [key, (obj1[key] || 0) + (obj2?.[key] || 0)]
      )
    )

  const mergeNestedQuestions = (obj1: {}, obj2: {}) => {
    const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})])

    return [...allKeys].reduce((acc, key) => {
      const val1 = obj1?.[key] || { asked: 0, recognized: 0 }
      const val2 = obj2?.[key] || { asked: 0, recognized: 0 }

      acc[key] = {
        asked: (val1.asked || 0) + (val2.asked || 0),
        recognized: (val1.recognized || 0) + (val2.recognized || 0)
      }

      return acc
    }, {})
  }

  const cursor = new Date(startDate)
  while (cursor <= endDate) {
    const key = getDateKey(cursor)
    const monthData = dataPerMonth[key]
    if (!monthData) {
      cursor.setMonth(cursor.getMonth() + 1)
      continue
    }
    const dailyStats = monthData.dailyStatistics

    const day = String(cursor.getDate()).padStart(2, '0')
    const month = String(cursor.getMonth() + 1).padStart(2, '0')
    const index = `${day}-${month}-${cursor.getFullYear()}`

    const stats = dailyStats[index]
    if (!stats) {
      cursor.setDate(cursor.getDate() + 1)
      continue
    }

    count++

    result.interactions += stats.interactions || 0
    result.uniqueUsers = new Set([...result.uniqueUsers, ...Object.keys(stats.users || {})])
    result.popularIntents = [...result.popularIntents, ...(stats.popularIntents || [])]
    result.sentiment += stats.sentiment || 0
    result.satisfaction += stats.satisfaction || 0
    result.retentionPerUser = combine(stats.users, result.retentionPerUser)
    result.chats += stats.chats || 0
    result.totalSeconds += (stats.seconds * stats.conversations) || 0
    result.conversations += stats.conversations || 0
    result.presentedRatings = [...result.presentedRatings, ...(stats.presentedRatings || [])]
    result.answeredRatingCards += stats.answeredRatingCards || 0
    result.ratingResults = combine(stats.ratingResults, result.ratingResults)
    result.channels = combine(stats.channels, result.channels)
    result.performance = combine(stats.performance, result.performance)
    result.interactionsPerUser = combine(stats.interactionsPerUser, result.interactionsPerUser)
    result.questionsPerNlu = mergeNestedQuestions(stats.questionsPerNlu, result.questionsPerNlu)
    result.trainedPerformance = combine(stats.trainedPerformance, result.trainedPerformance)
    result.untrainedPerformance = combine(stats.untrainedPerformance, result.untrainedPerformance)
    result.llmData = [...result.llmData, ...(stats.llmData || [])]

    cursor.setDate(cursor.getDate() + 1)
  }

  result.retention = Object.values(result.retentionPerUser || {}).filter((value: number) => value > 1).length || 0
  result.retention = result.retention > 0 ? (result.retention / result.uniqueUsers.size) * 100 : 0

  result.sentiment = (result.sentiment / count) || 0
  result.satisfaction = (result.satisfaction / count) || 0
  result.seconds = (result.totalSeconds / result.conversations) || 0

  return result
}

/**
 * Get the last twelve months data for the interactions and users for the chart
 * @returns {Array} The last twelve months data
 */
export function getLastTwelveMonthsData(dataPerMonth: any) {
  const monthlyData = []
  const end = new Date()
  const cursor = new Date(end.getFullYear(), end.getMonth() - 11, 1)

  while (cursor <= end) {
    const key = getDateKey(cursor)
    const monthData = dataPerMonth[key] || {}
    const stats = {
      label: months[cursor.getMonth()] + ' ' + cursor.getFullYear(),
      interactions: monthData?.interactions || 0,
      users: Object.keys(monthData?.users || {}).length || 0,
      month: cursor.getMonth()
    }

    monthlyData.push(stats)
    cursor.setMonth(cursor.getMonth() + 1)
  }

  return monthlyData
}


//Get all messages for the current month/year
export async function loadAllPeriodMessages(currentPeriod: Date[], chatRatings: { [key: string]: FabTypes[] }) {
  const [startDate, endDate] = currentPeriod
  const collectedRatings = []

  const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1)
  while (cursor <= endDate) {
    const month = cursor.getMonth()
    const year = cursor.getFullYear()
    const key = `${month}-${year}`

    if (chatRatings[key]) {
      collectedRatings.push(...chatRatings[key])
    }

    cursor.setMonth(cursor.getMonth() + 1)
  }

  const rangeStart = atStartOfDay(startDate)
  const rangeEnd = atEndOfDay(endDate)

  return collectedRatings.filter((message) => {
    const messageDate = new Date(message.timestamp)
    return messageDate >= rangeStart && messageDate <= rangeEnd
  })
}

export function getDateKey(date: Date): string {
  return `${date.getMonth()}-${date.getFullYear()}`
}


export function getInteractionsData(data: any) {
  return {
    interactions: data.interactions,
    questionsAsked: data.questionsAsked,
    questionsRecognized: data.questionsRecognized,
    touchlessHandling: data.touchlessHandling,
    startDate: data.startDate,
    endDate: data.endDate
  }
}